import {
  booleanAttribute,
  Component,
  computed,
  contentChild,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Menu, MenuItem } from '@angular/aria/menu';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import {
  isSeparator,
  MibMenuAlign,
  MibMenuEntry,
  MibMenuItem,
  MibMenuItemActionEvent,
  MibMenuItemTheme,
  MibMenuTriggerHost,
} from './menu.models';
import { MibIconComponent, MibIconName } from '../icons';
import { MibTooltipDirective } from '../tooltip';
import { MibSpinnerComponent } from '../loaders';
import { NgTemplateOutlet } from '@angular/common';
import { MibMenuItemContentDirective } from './menu-item-content.directive';
import { isBelowExpanded } from '../../internal/size-class.utils';

let nextId = 0;

/* A row's action or cross is a menu item of its own; its value is this object, a row's is a string. */
interface MenuRowControl {
  item: string;
  /** Absent on the cross. */
  action?: string;
  icon: MibIconName;
  label: string;
  theme?: MibMenuItemTheme;
}

interface MenuRow {
  item: MibMenuItem;
  controls: MenuRowControl[];
}

// The gap the board leaves between a trigger and its panel.
const MENU_OFFSET = 8;

const BELOW_START: ConnectedPosition = {
  originX: 'start',
  originY: 'bottom',
  overlayX: 'start',
  overlayY: 'top',
  offsetY: MENU_OFFSET,
};
const ABOVE_START: ConnectedPosition = {
  originX: 'start',
  originY: 'top',
  overlayX: 'start',
  overlayY: 'bottom',
  offsetY: -MENU_OFFSET,
};
const BELOW_END: ConnectedPosition = {
  originX: 'end',
  originY: 'bottom',
  overlayX: 'end',
  overlayY: 'top',
  offsetY: MENU_OFFSET,
};
const ABOVE_END: ConnectedPosition = {
  originX: 'end',
  originY: 'top',
  overlayX: 'end',
  overlayY: 'bottom',
  offsetY: -MENU_OFFSET,
};

/*
 Preference order, first that fits wins. Whichever edge is asked for, the other one is kept as a
 fallback: a trigger at the right of a bar has no room to open to the right, and the panel has to go
 somewhere.
*/
const POSITIONS: Record<MibMenuAlign, ConnectedPosition[]> = {
  start: [BELOW_START, ABOVE_START, BELOW_END, ABOVE_END],
  end: [BELOW_END, ABOVE_END, BELOW_START, ABOVE_START],
};

// Under the trigger, from the left edge of the screen.
const FULL_WIDTH: ConnectedPosition[] = [
  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: MENU_OFFSET },
];

/**
 * The menu panel: a list of items shown in an overlay anchored to whatever opened it.
 *
 * It has no trigger of its own. Put `[mibMenuTrigger]` on the element that should open it — a
 * `[mibButton]`, an avatar, a bare `button` — and configure that element with its own API rather than
 * through the panel.
 *
 * ```html
 * <button mibButton [mibMenuTrigger]="actions" aria-label="More options">
 *   <svg mib-icon name="ellipsis-vertical"></svg>
 * </button>
 * <mib-menu #actions [menuItems]="items" (itemSelected)="onSelect($event)" />
 * ```
 */
@Component({
  selector: 'mib-menu',
  exportAs: 'mibMenu',
  imports: [
    Menu,
    MenuItem,
    OverlayModule,
    NgTemplateOutlet,
    MibIconComponent,
    MibSpinnerComponent,
    MibTooltipDirective,
  ],
  templateUrl: './menu.component.html',
  /*
   The panel lives in an overlay, so the element itself draws nothing — but it still counted as a
   child of whatever it was written in, and a flex or grid parent reserved a gap for it. A row of
   actions ended up with 8px of emptiness beside its button, and the button looked off-centre.
   `contents` takes the box away and leaves the behaviour.
  */
  host: { class: 'contents' },
  styleUrls: ['../../internal/spinner-leave.css', './menu.component.css'],
})
export class MibMenuComponent {
  /** The entries of the panel, each a `MibMenuItem` or a `MibMenuSeparator`. */
  readonly menuItems = input.required<MibMenuEntry[]>();

  /** The row template projected as `<ng-template mibMenuItemContent>`; absent, rows draw their icon and label. */
  protected readonly itemContent = contentChild(MibMenuItemContentDirective);
  /** Edge of the trigger the panel lines up with. */
  readonly align = input<MibMenuAlign>('start');

  /** Draws the panel dark; it already is when its trigger sits inside a `.dark` ancestor. */
  readonly darkMode = input(false, { transform: booleanAttribute });

  /** Draws the panel at its full 320px rather than as wide as its longest row. */
  readonly wide = input(false, { transform: booleanAttribute });

  /** Below the expanded size class, opens the panel as a card 8px from both screen edges, 8px under its trigger. */
  readonly fullWidthOnPhone = input(false, { transform: booleanAttribute });

  /** Draws a selected row navy and semibold on the branding-50 tint, with a 3px primary bar on its left. */
  readonly markSelected = input(false, { transform: booleanAttribute });

  /** The verb every cross says to a screen reader, before its row's label. */
  readonly removeLabel = input('Remove');

  /** What a screen reader adds to an item that is loading, beside its label rather than in it. */
  readonly loadingLabel = input('Loading');

  protected readonly loadingId = `mib-menu-loading-${nextId++}`;

  /** Emitted with the `value` of the activated leaf `MibMenuItem`. */
  readonly itemSelected = output<string>();
  /** Asks for a row to be removed, by its value. The panel takes nothing out of the list itself. */
  readonly itemRemoved = output<string>();
  /** Emitted when a row's action is pressed; the row is not selected and the panel stays open. */
  readonly itemAction = output<MibMenuItemActionEvent>();

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly panel = viewChild<ElementRef<HTMLElement>>('menuPanel');
  private readonly trigger = signal<MibMenuTriggerHost | null>(null);

  /** Whether the panel is open, as its trigger says. */
  readonly isOpen = computed(() => this.trigger()?.expanded() ?? false);

  /** Falls back to the host so the overlay always has an origin, even before a trigger registers. */
  protected readonly origin = computed(() => this.trigger()?.elementRef ?? this.host);

  /* The overlay sits outside the trigger's DOM, so a `.dark` ancestor is looked up at every open. */
  protected readonly panelIsDark = computed(
    () => this.darkMode() || (this.isOpen() && !!this.origin().nativeElement.closest('.dark')),
  );

  /* Queried at each open and only when asked. */
  protected readonly isFullWidth = computed(() => this.isOpen() && this.fullWidthOnPhone() && isBelowExpanded());

  /* A point at x 0 whose y is read live, so the CDK's repositioning on scroll and resize still follows the trigger. */
  protected readonly overlayOrigin = computed(() => {
    const origin = this.origin();
    if (!this.isFullWidth()) return origin;
    return {
      x: 0,
      get y(): number {
        return origin.nativeElement.getBoundingClientRect().bottom;
      },
    };
  });

  protected readonly menuPositions = computed(() => (this.isFullWidth() ? FULL_WIDTH : POSITIONS[this.align()]));

  /*
   The rows are inset from the panel edge, so a separator cannot be a row of its own any more: it is
   the boundary between two padded groups, and the line is drawn edge to edge between them. Splitting
   here rather than in the template also takes the separator narrowing out of the markup.
  */
  protected readonly itemGroups = computed<MenuRow[][]>(() => {
    const groups: MenuRow[][] = [[]];
    for (const entry of this.menuItems()) {
      if (isSeparator(entry)) {
        groups.push([]);
      } else {
        groups[groups.length - 1].push({ item: entry, controls: this.controlsOf(entry) });
      }
    }
    return groups.filter((group) => group.length > 0);
  });

  /* The row's label is in each name: the control sits outside the row and is read on its own. */
  private controlsOf(item: MibMenuItem): MenuRowControl[] {
    const controls: MenuRowControl[] = (item.actions ?? []).map((action) => ({
      item: item.value,
      action: action.value,
      icon: action.icon,
      label: `${action.label} ${item.label}`,
      theme: action.theme,
    }));
    if (item.removable) {
      controls.push({ item: item.value, icon: 'x', label: `${this.removeLabel()} ${item.label}` });
    }
    return controls;
  }

  /** Called by `[mibMenuTrigger]`. A panel is driven by one trigger at a time. */
  attachTrigger(trigger: MibMenuTriggerHost): void {
    this.trigger.set(trigger);
  }

  /** Closes the panel and hands focus back to the trigger. */
  close(): void {
    const trigger = this.trigger();
    trigger?.close();
    trigger?.focus();
  }

  /* An action or a cross selects nothing and leaves the panel open. */
  protected onItemSelected(value: string | MenuRowControl): void {
    if (typeof value === 'string') {
      this.itemSelected.emit(value);
      this.close();
    } else if (value.action === undefined) {
      this.removeItem(value.item);
    } else {
      this.itemAction.emit({ item: value.item, action: value.action });
    }
  }

  /* Focus goes to the panel first: the row that had it is about to leave the DOM. */
  private removeItem(value: string): void {
    this.panel()?.nativeElement.focus();
    this.itemRemoved.emit(value);
  }

  /* Delete asks for the row the way its cross does, so a keyboard reader is not sent hunting for it. */
  protected onItemKeydown(event: KeyboardEvent, item: MibMenuItem): void {
    if (!item.removable || (event.key !== 'Delete' && event.key !== 'Backspace')) return;
    event.preventDefault();
    this.removeItem(item.value);
  }

  /*
   Focus has to be moved into the panel for the arrow keys to reach the list. The menu element takes
   it when it is focusable; otherwise the first item does, which is what a menu owes a keyboard user
   either way.
  */
  protected onOpened(): void {
    const panel = this.panel()?.nativeElement;
    if (!panel) return;
    panel.focus();
    if (!panel.contains(document.activeElement)) {
      panel.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    }
  }

  /* `ngMenu` handles Escape first and prevents it, so the overlay's own keydown never sees it. */
  protected onEscape(event: Event): void {
    event.preventDefault();
    this.close();
  }

  /* Only Escape reaches `ngMenu` from the footer: Enter or Space there would select the active row. */
  protected onFooterKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') event.stopPropagation();
  }

  protected onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  protected onOutsideClick(event: MouseEvent): void {
    // A click on the trigger is its own business: it toggles, and closing here too would reopen it.
    const trigger = this.trigger()?.elementRef.nativeElement;
    if (trigger?.contains(event.target as Node)) return;
    this.trigger()?.close();
  }

  // ── Tailwind class sets ───────────────────────────────────────────────────

  /*
   The panel is a floating surface of the design system, not a button: it takes its colours from the
   palette rather than from the `mib-btn-*` internals it used to borrow. Its edge is the divider and
   not the hairline — the board draws the edge of a floating surface one step lighter than the border
   of a field — and the corner is the 4px of the system, whatever radius the design file carries.

   The panel is bounded rather than sized — as wide as its longest row up to 320px, then the rows are
   cut, or 320px outright when `wide`; as tall as its list up to 320px, then the list scrolls between a header and a footer that stay — so a long list of long names stays a panel.
   With `fullWidthOnPhone` on a phone, it spans the screen 8px in from each edge, its rows inset by 6px.
  */
  protected readonly panelClasses = computed(
    () =>
      'mib-menu-panel flex flex-col min-w-40 max-h-80 overflow-hidden border border-mib-divider bg-white ' +
      // Important, as the listbox panel's: a host app's unlayered `.bg-white` outranks the library.
      'dark:bg-mib-background-dark! dark:border-mib-border-dark ' +
      // `0 4px 6px -1px rgba(0,0,0,.1)`, which is what the board draws under a panel — and `shadow-md`.
      'shadow-md ' +
      this.panelShape(),
  );

  private panelShape(): string {
    if (this.isFullWidth()) return 'flex-1 mx-2 p-1.5 rounded-mib-lg';
    return this.wide() ? 'w-80 max-w-80 rounded-mib-lg' : 'max-w-80 rounded-mib-lg';
  }

  /*
   A row is measured by its inset rather than given a height: 14px across and 10px down around a
   `text-sm` label, which is Inter Medium 14/20, so the row comes out at 40px without asserting a
   number. That 14px is the whole inset — the group around it adds none — so a row's label lines up
   with the header's. The label is the gray-900 ink of the board, and the fill under the pointer is
   the gray-100 a secondary button takes on hover, drawn as a full-width band since the row runs
   from edge to edge.

   Its icon is `size-5` — 20px, the step a control of this height already gets inside a button, so
   an icon reads at the same size wherever the system puts one.

   The row under the pointer or under the arrow keys is highlighted with a background, never a ring:
   `data-active` follows the pointer, and the menu moves DOM focus onto the row it points at, so both
   an `outline` on `data-active` and one on `focus-within` end up drawn as a border on hover. The
   ring is therefore bound to `:focus-visible`, which the browser withholds when focus came from a
   pointer. The background highlight steps aside for a selected row, which keeps its navy tint.
  */
  protected itemClasses(item: MibMenuItem): string {
    const marked = !!item.selected && this.markSelected();
    return (
      'flex min-w-0 flex-1 items-center gap-3 cursor-pointer px-3.5 py-2.5 text-sm text-left select-none ' +
      (marked ? 'font-semibold ' : 'font-medium ') +
      this.inkClasses(item, marked) +
      'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mib-primary/50 dark:focus-visible:outline-mib-primary ' +
      'has-[:focus-visible]:outline-2 has-[:focus-visible]:-outline-offset-2 has-[:focus-visible]:outline-mib-primary/50 dark:has-[:focus-visible]:outline-mib-primary ' +
      'aria-disabled:opacity-40 aria-disabled:cursor-not-allowed aria-disabled:pointer-events-none'
    );
  }

  private inkClasses(item: MibMenuItem, marked: boolean): string {
    if (item.theme === 'danger') return 'text-mib-danger dark:text-mib-danger-dark ';
    if (marked) return 'text-mib-branding-900 dark:text-white ';
    return 'text-gray-900 aria-[current=true]:text-mib-primary dark:text-white dark:aria-[current=true]:text-white ';
  }

  /*
   The fill is drawn on the row and not on the item, so it runs under the cross too. The row under
   the pointer or under the arrow keys takes the gray-100 of a control on hover; a selected row keeps
   its navy tint whatever the pointer does. A disabled row takes nothing, and keeps its tooltip.
  */
  protected rowClasses(item: MibMenuItem): string {
    const marked = item.selected && this.markSelected();
    const selectedFill = marked ? 'bg-mib-branding-50 dark:bg-gray-700' : 'bg-mib-indigo-50 dark:bg-gray-700';
    return (
      'group/row flex items-stretch ' +
      (this.isFullWidth() ? 'rounded-mib overflow-hidden ' : '') +
      (marked ? 'relative before:absolute before:inset-y-0 before:left-0 before:w-0.75 before:bg-mib-primary ' : '') +
      (item.selected
        ? selectedFill
        : item.disabled
          ? ''
          : item.theme === 'danger'
            ? 'hover:bg-mib-danger-surface has-[[data-active=true]]:bg-mib-danger-surface dark:hover:bg-white/5 dark:has-[[data-active=true]]:bg-white/5'
            : 'hover:bg-gray-100 has-[[data-active=true]]:bg-gray-100 dark:hover:bg-white/5 dark:has-[[data-active=true]]:bg-white/5')
    );
  }

  /* gray-500 drops under 4.5:1 on the gray-100 band, so a lit row's sub-label takes gray-700. */
  protected subLabelClasses(item: MibMenuItem): string {
    if (item.selected) return 'text-gray-700 dark:text-gray-300';
    return (
      'text-gray-500 group-hover/row:text-gray-700 group-has-[[data-active=true]]/row:text-gray-700 ' +
      'dark:text-gray-400 dark:group-hover/row:text-gray-400 dark:group-has-[[data-active=true]]/row:text-gray-400'
    );
  }

  /* Beside the row and not inside it: a menu item inside a menu item is a control nested in a control. A danger action takes the ink and the hover a danger row does. */
  protected controlClasses(control: MenuRowControl): string {
    const ink =
      control.theme === 'danger'
        ? 'text-mib-danger hover:bg-mib-danger-surface dark:text-mib-danger-dark dark:hover:bg-white/5'
        : 'text-gray-500 hover:bg-gray-200 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white';
    return `flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-mib outline-mib-primary focus-visible:outline-2 ${ink}`;
  }
}
